import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { Severity, SignalKind } from "@backstory/protocol";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { ErrorState } from "../../../components/States";
import { AutoLoadMore, PageHeader, useToast } from "../../../components/ui";
import { formatDateTime, formatRelative } from "../../../lib/format";
import { api, queryKeys } from "../../../api";
import { insightsApi, insightsKeys, type IssuePatch, type IssuesQuery } from "../api";
import { Sparkline } from "../charts";
import { ALL_KINDS, AffectedUsers, AssigneeSelect, isSnoozed, issueIdentity, RangePicker, RegressionBadge, SEVERITIES, STATUSES, SeverityBadge, StatusBadge, kindLabel, statusLabel, useRange } from "../components";
import { rangeLabel } from "../range";

type Filters = Pick<IssuesQuery, "q" | "kind" | "severity" | "status" | "route" | "release" | "assignee" | "sort">;
const FILTER_KEYS: Array<keyof Filters> = ["q", "kind", "severity", "status", "route", "release", "assignee", "sort"];
const SORTS = new Set<Filters["sort"]>(["occurrences", "impact", "newest", "severity"]);

export function filtersFromSearch(s: Record<string, unknown>): Filters {
  const f: Filters = {};
  for (const k of FILTER_KEYS) {
    if (typeof s[k] !== "string" || s[k] === "") continue;
    if (k === "sort" && !SORTS.has(s[k] as Filters["sort"])) continue;
    (f as Record<string, unknown>)[k] = s[k];
  }
  return f;
}

export function IssuesPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const can = useCan();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const filters = useMemo(() => filtersFromSearch(search), [search]);
  const { range, state: rangeState } = useRange();
  const meEmail = auth.me?.user.email;
  const qc = useQueryClient();
  const toast = useToast();
  const [qInput, setQInput] = useState(filters.q ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [snooze, setSnooze] = useState("24");
  const [assignee, setAssignee] = useState("");

  const setFilters = (patch: Partial<Filters>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === "") delete next[k];
    void navigate({ to: "/issues", search: next as never, replace: true });
  };
  useEffect(() => setQInput(filters.q ?? ""), [filters.q]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = qInput.trim() || undefined;
      if (q !== filters.q) setFilters({ q });
    }, 250);
    return () => window.clearTimeout(timer);
    // setFilters deliberately follows the current URL-derived filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput, filters.q]);

  const q = useInfiniteQuery({
    queryKey: insightsKeys.issues({ ...filters, ...range }, auth.currentProjectId),
    queryFn: ({ pageParam }) => insightsApi.issues({ ...filters, ...range, cursor: pageParam as string | undefined, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const releases = useQuery({
    queryKey: [...insightsKeys.releases(range, auth.currentProjectId), "issues-filter"],
    queryFn: () => insightsApi.releases(range),
    enabled: !!auth.currentProjectId,
    retry: 0,
  });

  const members = useQuery({ queryKey: queryKeys.members(auth.currentOrgId ?? ""), queryFn: () => api.orgs.members(auth.currentOrgId!), enabled: !!auth.currentOrgId, staleTime: 60_000 });
  const memberNames = useMemo(() => new Map((members.data ?? []).filter((m) => m.name).map((m) => [m.email, m.name!])), [members.data]);

  const patch = useMutation({
    mutationFn: async ({ ids, body }: { ids: string[]; body: IssuePatch }) => Promise.all(ids.map((id) => insightsApi.patchIssue(id, body))),
    onSuccess: (_d, v) => {
      toast.push("success", `${v.ids.length} cluster${v.ids.length === 1 ? "" : "s"} updated`);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["insights", "issues"] });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const bulk = (body: IssuePatch) => patch.mutate({ ids: Array.from(selected), body });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <PageHeader title="Issues" actions={<RangePicker />}>
        <div className="ins-filters" role="search" aria-label="Issue filters">
          <label className="issues-search">
            Search
            <input
              placeholder="Title, route, selector, error…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              aria-label="Search issues"
              autoComplete="off"
            />
          </label>
          <label>
            Kind
            <select value={filters.kind ?? ""} onChange={(e) => setFilters({ kind: (e.target.value || undefined) as SignalKind | undefined })} autoComplete="off">
              <option value="">All kinds</option>
              {ALL_KINDS.map((k) => (<option key={k} value={k}>{kindLabel(k)}</option>))}
            </select>
          </label>
          <label>
            Severity
            <select value={filters.severity ?? ""} onChange={(e) => setFilters({ severity: (e.target.value || undefined) as Severity | undefined })} autoComplete="off">
              <option value="">Any severity</option>
              {SEVERITIES.map((s) => (<option key={s} value={s}>{statusLabel(s)}</option>))}
            </select>
          </label>
          <label>
            Status
            <select value={filters.status ?? ""} onChange={(e) => setFilters({ status: (e.target.value || undefined) as Filters["status"] })} autoComplete="off" title="Any status leaves out muted and snoozed issues">
              <option value="">Any status</option>
              {STATUSES.map((s) => (<option key={s} value={s}>{statusLabel(s)}</option>))}
              <option value="snoozed">Snoozed</option>
            </select>
          </label>
          <label>
            Route
            <input placeholder="/patients/:id" value={filters.route ?? ""} onChange={(e) => setFilters({ route: e.target.value || undefined })} autoComplete="off" />
          </label>
          <label>
            Release
            <select value={filters.release ?? ""} onChange={(e) => setFilters({ release: e.target.value || undefined })} autoComplete="off">
              <option value="">Any release</option>
              {releases.data?.map((r) => <option key={r.release} value={r.release}>{r.release}</option>)}
              {filters.release && !releases.data?.some((r) => r.release === filters.release) && <option value={filters.release}>{filters.release}</option>}
            </select>
          </label>
          <label>
            Owner
            <select value={filters.assignee ?? ""} onChange={(e) => setFilters({ assignee: e.target.value || undefined })} autoComplete="off">
              <option value="">Anyone</option>
              <option value="unassigned">Unassigned</option>
              {meEmail && <option value={meEmail}>Assigned to me</option>}
              {(members.data ?? []).filter((m) => m.email !== meEmail).map((m) => (
                <option key={m.email} value={m.email}>{m.name || m.email}</option>
              ))}
              {filters.assignee && filters.assignee !== "unassigned" && filters.assignee !== meEmail && !(members.data ?? []).some((m) => m.email === filters.assignee) && (
                <option value={filters.assignee}>{filters.assignee}</option>
              )}
            </select>
          </label>
          <label>
            Sort
            <select value={filters.sort ?? ""} onChange={(e) => setFilters({ sort: (e.target.value || undefined) as Filters["sort"] })} autoComplete="off">
              <option value="">Last seen</option>
              <option value="newest">Newest</option>
              <option value="occurrences">Most occurrences</option>
              <option value="impact">Most people affected</option>
              <option value="severity">Severity</option>
            </select>
          </label>
          {Object.keys(filters).length > 0 && (
            <button type="button" className="btn ghost" onClick={() => void navigate({ to: "/issues", search: { range: search.range, from: search.from, to: search.to } as never, replace: true })}>
              Clear
            </button>
          )}
        </div>
      </PageHeader>

      {selected.size > 0 && (
        <div className="ins-bulk" role="toolbar" aria-label="Bulk actions">
          <strong>{selected.size} selected</strong>
          <button type="button" className="btn" onClick={() => bulk({ status: "acknowledged" })}>Acknowledge</button>
          <button type="button" className="btn" onClick={() => bulk({ status: "resolved" })}>Resolve</button>
          <button type="button" className="btn" onClick={() => bulk({ status: "muted" })}>Mute</button>
          <label className="small muted" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            Snooze
            <select className="input" style={{ minWidth: 90, height: 28 }} value={snooze} onChange={(e) => setSnooze(e.target.value)} aria-label="Snooze duration" autoComplete="off">
              <option value="1">1 hour</option>
              <option value="24">24 hours</option>
              <option value="168">1 week</option>
            </select>
            <button type="button" className="btn" onClick={() => bulk({ snoozedUntil: new Date(Date.now() + Number(snooze) * 3600_000).toISOString() })}>Apply</button>
          </label>
          <label className="small muted" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            Assign
            <AssigneeSelect className="input" style={{ minWidth: 180, height: 28 }} value={assignee} onChange={setAssignee} unassigned="Choose a member…" />
            <button type="button" className="btn" disabled={!assignee} onClick={() => bulk({ assignee })}>Assign</button>
          </label>
          <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>Cancel</button>
        </div>
      )}

      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : (
        <div className="card table-scroll">
          <table className="table issues-table" aria-label="Issue clusters" aria-busy={q.isPending || undefined}>
            {/* Column widths live in styles.css so they can change with the screen. */}
            <colgroup>
              <col /><col /><col /><col /><col /><col />
            </colgroup>
            <thead>
              <tr>
                <th>{can.write && <input type="checkbox" aria-label="Select all clusters" checked={allSelected} onChange={toggleAll} autoComplete="off" />}</th>
                <th>Issue</th>
                <th>Occurrences</th>
                <th>Impact</th>
                <th>Last seen</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => void navigate({ to: "/issues/$clusterId", params: { clusterId: c.id } })} onKeyDown={(e) => e.key === "Enter" && void navigate({ to: "/issues/$clusterId", params: { clusterId: c.id } })} tabIndex={0} data-testid="issue-row">
                  <td onClick={(e) => e.stopPropagation()}>{can.write && <input type="checkbox" aria-label={`Select ${c.title}`} checked={selected.has(c.id)} onChange={() => toggle(c.id)} autoComplete="off" />}</td>
                  <td className="issues-main-cell">
                    <span className="ins-title">{c.title}</span>
                    {issueIdentity(c) && <span className="ins-where">{issueIdentity(c)}</span>}
                    <span className="issues-badges">
                      <SeverityBadge severity={c.severity} />
                      <StatusBadge status={c.status} />
                      {isSnoozed(c.snoozedUntil) && <span className="badge neutral" title={`Snoozed until ${formatDateTime(c.snoozedUntil!)}`}>Snoozed</span>}
                      <RegressionBadge cluster={c} />
                    </span>
                    <span className="ins-sub">
                      {kindLabel(c.kind)}
                      {c.errorFingerprint ? <> · <span className="mono">{c.errorFingerprint}</span></> : null}
                    </span>
                  </td>
                  <td data-label="Occurrences">
                    <span className="cell-stack">
                      {c.countInRange != null ? (
                        <>
                          <strong className="mono" title={rangeLabel(rangeState)}>{c.countInRange.toLocaleString()}</strong>
                          <small>{c.count.toLocaleString()} all time</small>
                        </>
                      ) : (
                        <>
                          <strong className="mono">{c.count.toLocaleString()} total</strong>
                          <small>{c.count24h.toLocaleString()} / 24h · {c.count7d.toLocaleString()} / 7d</small>
                        </>
                      )}
                    </span>
                    <Sparkline values={c.trend ?? []} label={`${c.title} hourly trend`} />
                  </td>
                  <td data-label="Impact" title={c.countInRange != null ? rangeLabel(rangeState) : undefined}>
                    <AffectedUsers users={c.topUsers} count={c.affectedUsers} sessions={c.affectedSessions} />
                  </td>
                  <td className="small" data-label="Last seen" title={`${c.firstSeenAt} → ${c.lastSeenAt}`}>
                    <span className="cell-stack">
                      <strong>{formatRelative(c.lastSeenAt)}</strong>
                      <small>first {formatRelative(c.firstSeenAt)}</small>
                    </span>
                  </td>
                  <td data-label="Owner">
                    <span className="cell-stack">
                      <strong title={c.assignee}>{c.assignee ? (memberNames.get(c.assignee) ?? c.assignee) : <span className="muted">Unassigned</span>}</strong>
                      <small title="Release where this issue was first seen">{c.firstRelease || c.releasesSeen[0] || "No release"}</small>
                    </span>
                  </td>
                </tr>
              ))}
              {!q.isPending && rows.length === 0 && (
                <tr className="static"><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>No issues match these filters.</td></tr>
              )}
              {q.isPending && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="static" aria-hidden="true"><td colSpan={6}><span className="skeleton">loading</span></td></tr>
              ))}
            </tbody>
          </table>
          <AutoLoadMore hasMore={!!q.hasNextPage} loading={q.isFetchingNextPage} loadMore={() => void q.fetchNextPage()} label="Loading more issues" />
        </div>
      )}
    </>
  );
}
